import DeleteIcon from '@offline-mui/icons-material/Delete'
import EditIcon from '@offline-mui/icons-material/Edit'
import ExpandMoreIcon from '@offline-mui/icons-material/ExpandMore'
import AccessTimeIcon from '@offline-mui/icons-material/AccessTime'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from '@offline-mui/material'
import type { FlightsData } from '../api/inventory'
import {
  airlineName,
  airportLabel,
  formatClockTime,
  formatDuration,
  formatIsoDate,
} from '../utils/flightFormat'

type FlightDataAccordionItemProps = {
  flight: FlightsData
  defaultExpanded?: boolean
  onEdit: (flight: FlightsData) => void
  onDelete: (flight: FlightsData) => void
}

export default function FlightDataAccordionItem({
  flight,
  defaultExpanded = false,
  onEdit,
  onDelete,
}: FlightDataAccordionItemProps) {
  const operatedBy =
    flight.operating_carrier_name ||
    airlineName(flight.operating_carrier || flight.carrier)

  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      sx={{
        '&:before': { display: 'none' },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2,
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            my: 1.25,
            alignItems: 'center',
            overflow: 'hidden',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{
            flex: 1,
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            pr: 1,
            minWidth: 0,
          }}
        >
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexShrink: 0 }}>
            <Box
              sx={{
                minWidth: 40,
                height: 28,
                px: 1,
                borderRadius: 0.5,
                bgcolor: 'error.dark',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {flight.carrier}
            </Box>
            <Typography sx={{ fontWeight: 800 }}>
              {flight.carrier} {flight.flight_number}
            </Typography>
          </Stack>

          <Typography sx={{ fontWeight: 700 }}>
            {flight.departure_airport}-{flight.arrival_airport}
          </Typography>

          <Typography sx={{ fontWeight: 600, flexShrink: 0 }}>
            {formatIsoDate(flight.departure_datetime)}{' '}
            {formatClockTime(flight.departure_datetime)} →{' '}
            {formatClockTime(flight.arrival_datetime)}
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2, py: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'baseline' }, gap: 0.5 }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                {airlineName(flight.carrier)} {flight.carrier} {flight.flight_number}
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                Aircraft : {flight.aircraft_type || '—'}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Operated by : {operatedBy}
              {flight.aircraft_type ? ` | ${flight.aircraft_type}` : ''}
              {` | Block ${flight.itinerary_id}`}
              {` | Segment ${flight.segment_id}`}
            </Typography>

            <Stack
              direction="row"
              sx={{ mt: 2, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {airportLabel(flight.departure_airport)}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
                  {formatClockTime(flight.departure_datetime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatIsoDate(flight.departure_datetime)}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', flexShrink: 0, px: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatDuration(flight.duration_min)}
                </Typography>
                <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
                <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {airportLabel(flight.arrival_airport)}
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
                  {formatClockTime(flight.arrival_datetime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatIsoDate(flight.arrival_datetime)}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              px: 2,
              py: 1,
              bgcolor: 'grey.100',
              justifyContent: 'space-between',
              alignItems: { sm: 'center' },
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Flight ID {flight.id}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit(flight)}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(flight)}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
